import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '../../../hooks';
import { visualEditorFetchOffersActions } from '../state';
import { useVisualEditorProvider } from '../context';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { TextSearchItemsPayload } from '../types/text-search';

export const usePreviewTextSearch = (shopId: number) => {
  const { offers, error, loading, total } = useAppSelector(({ visualEditor }) => visualEditor);
  const { dispatchOffersThunk, getPayload } = useVisualEditorProvider();
  const [showLoadingResults, setShowLoadingResults] = useState<boolean>(false);

  const onRefresh = useCallback(() => {
    const payload = getPayload<TextSearchItemsPayload>();
    if (!payload) return;
    setShowLoadingResults(true);
    dispatchOffersThunk(visualEditorFetchOffersActions.getTextSearchItems, { ...payload, from: 0 })
      .unwrap()
      .finally(() => setShowLoadingResults(false));
  }, [dispatchOffersThunk, getPayload]);

  const onLoadMore = useCallback(() => {
    const payload = getPayload<TextSearchItemsPayload>();
    if (!offers.length || !payload) return;
    dispatchOffersThunk(visualEditorFetchOffersActions.getTextSearchItems, {
      ...payload,
      from: offers.length,
      shopId,
    });
  }, [getPayload, offers.length, dispatchOffersThunk]);

  const hasNextPage = !error && !!offers.length && offers.length < total;
  const showLoader = hasNextPage;

  const [sentryRef, { rootRef, isVisible }] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
    disabled: error,
    delayInMs: 300,
  });

  const infiniteScroll = useMemo(
    () => ({ sentryRef, rootRef, showLoader }),
    [sentryRef, rootRef, showLoader]
  );

  const displayedOffers = showLoadingResults ? [] : offers;
  const disableRefresh = loading || isVisible;

  return {
    onRefresh,
    displayedOffers,
    disableRefresh,
    infiniteScroll,
  };
};
